// src/app/router/index.tsx
import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import AppLayout from '@shared/layouts/AppLayout';
import AppLayoutAuth from '@shared/layouts/AppLayoutAuth';
import PublicLayout from '@shared/layouts/PublicLayout';
import { PERMISSIONS } from '@core/constants/permissions';
import { lazyWithRetry as lazy } from '@shared/utils/lazyWithRetry';
import AdminRoute from '@features/admin/router/AdminRoute';
import AdminLayout from '@features/admin/layout/AdminLayout';

// Lazy load components for code splitting
const AdminDashboard = lazy(() => import('@features/admin/pages/AdminDashboard'));
const AdminUsers = lazy(() => import('@features/admin/pages/AdminUsers'));
const AdminAdmins = lazy(() => import('@features/admin/pages/AdminAdmins'));
const AdminRoles = lazy(() => import('@features/admin/pages/AdminRoles'));
const AdminPermissions = lazy(() => import('@features/admin/pages/AdminPermissions'));
const AdminSettings = lazy(() => import('@features/admin/pages/AdminSettings'));
const AdminLogs = lazy(() => import('@features/admin/pages/AdminLogs'));
const UserActivity = lazy(() => import('@features/users/pages/UserActivity'));
const ChangePassword = lazy(() => import('@features/users/pages/ChangePassword'));
const AdminTickets = lazy(() => import('@features/admin/pages/AdminTickets'));
const Login = lazy(() => import('@features/auth/pages/Login'));
const HomeEntry = lazy(() => import('@features/landing/HomeEntry'));
const LandingPage = lazy(() => import('@features/landing/LandingPage'));
const RegisterUser = lazy(() => import('@features/auth/pages/RegisterUser'));
const RegisterCompany = lazy(() => import('@features/auth/pages/RegisterCompany'));
const RegisterFactory = lazy(() => import('@features/auth/pages/RegisterFactory'));
const WaybillsList = lazy(() => import('@features/waybills/pages/WaybillsList'));
const TrashPage = lazy(() => import('@features/waybills/pages/TrashPage'));
const RemittanceList = lazy(() => import('@features/remittance/pages/RemittanceList'));
const RemittanceDrafts = lazy(() => import('@features/remittance/pages/RemittanceDrafts'));
const CreateWaybill = lazy(() => import('@features/waybills/pages/Waybill'));
const ViewWaybill = lazy(() => import('@features/waybills/pages/ViewWaybill'));
const Remittance = lazy(() => import('@features/remittance/pages/Remittance'));
const DriversList = lazy(() => import('@features/drivers/pages/DriversList'));
const CreateCompany = lazy(() => import('@features/companies/pages/CreateCompany'));
const CitiesList = lazy(() => import('@features/cities/pages/CitiesList'));
const CarsList = lazy(() => import('@features/cars/pages/CarsList'));
const ReportsList = lazy(() => import('@features/reports/pages/ReportsList'));
const TonnageReport = lazy(() => import('@features/reports/pages/TonnageReport'));
const BranchesList = lazy(() => import('@features/branches/pages/BranchesList'));
const OfficesList = lazy(() => import('@features/offices/pages/OfficesList'));
const GoodsList = lazy(() => import('@features/goods/pages/GoodsList'));
const ProductOwnersList = lazy(() => import('@features/product-owners/pages/ProductOwnersList'));
const RemittanceTracking = lazy(() => import('@features/remittance/pages/RemittanceTracking'));
const DriverDetail = lazy(() => import('@features/drivers/pages/DriverDetail'));
const CompanyDetail = lazy(() => import('@features/companies/pages/CompanyDetail'));
const WaybillHistory = lazy(() => import('@features/waybills/pages/WaybillHistory'));
const WaybillPayments = lazy(() => import('@features/waybills/pages/WaybillPayments'));
const RemittanceHistory = lazy(() => import('@features/remittance/pages/RemittanceHistory'));
const RemittancePayments = lazy(() => import('@features/remittance/pages/RemittancePayments'));
const DashboardWithTabs = lazy(() => import('@features/dashboard/pages/DashboardWithTabs'));
const WalletPage = lazy(() => import('@features/wallet/pages/WalletPage'));
const SenderReceiverSearch = lazy(() => import('@features/sender-receiver-search/pages/SenderReceiverSearch'));
const PrintWaybill = lazy(() => import('@features/waybills/pages/PrintWaybill'));
const PrintRemittance = lazy(() => import('@features/remittance/pages/PrintRemittance'));
const RemittanceDischarge = lazy(() => import('@features/remittance/pages/RemittanceDischarge'));
const DriverTurns = lazy(() => import('@features/drivers/pages/DriverTurns'));
const DriverCars = lazy(() => import('@features/drivers/pages/DriverCars'));
const CompanyUsers = lazy(() => import('@features/companies/pages/CompanyUsers'));
const SummaryReports = lazy(() => import('@features/reports/pages/SummaryReports'));
const SpecialSummaryReport = lazy(() => import('@features/reports/pages/SpecialSummaryReport'));
const PayTheFareReport = lazy(() => import('@features/reports/pages/PayTheFareReport'));
const OperationalWaybillReport = lazy(() => import('@features/reports/pages/OperationalWaybillReport'));
const WaybillFinancialReport = lazy(() => import('@features/reports/pages/WaybillFinancialReport'));
const DriverOffline = lazy(() => import('@features/drivers/pages/DriverOffline'));
const CompanyProfile = lazy(() => import('@features/companies/pages/CompanyProfile'));
const CompanyAdmins = lazy(() => import('@features/companies/pages/CompanyAdmins'));
const FinancialCoefficients = lazy(() => import('@features/financial-coefficients/pages/FinancialCoefficients'));
const CreateFinancialCoefficient = lazy(() => import('@features/financial-coefficients/pages/CreateFinancialCoefficient'));
const EditFinancialCoefficient = lazy(() => import('@features/financial-coefficients/pages/EditFinancialCoefficient'));
const InsuranceContracts = lazy(() => import('@features/insurance-contracts/pages/InsuranceContracts'));
const InsuranceContractEditor = lazy(() => import('@features/insurance-contracts/pages/InsuranceContractEditor'));
const WaybillNumbers = lazy(() => import('@features/waybill-numbers/pages/WaybillNumbers'));
const DriversSearch = lazy(() => import('@features/drivers-search/pages/DriversSearch'));
const Settings = lazy(() => import('@features/settings/pages/Settings'));
const PrintStamps = lazy(() => import('@features/settings/pages/PrintStamps'));
const PrintStampAudit = lazy(() => import('@features/settings/pages/PrintStampAudit'));
const CompanyUsersList = lazy(() => import('@features/users/pages/CompanyUsersList'));
const RolesList = lazy(() => import('@features/roles/pages/RolesList'));
const Endpoints = lazy(() => import('@features/endpoints/pages/Endpoints'));
const SurMap = lazy(() => import('@features/waybills/pages/SurMap'));
const CustomersList = lazy(() => import('@features/customers/pages/CustomersList'));
const SecondaryDebtSettings = lazy(() => import('@features/customers/pages/SecondaryDebtSettings'));
const ChooseWaybillDebtor = lazy(() => import('@features/customers/pages/ChooseWaybillDebtor'));
const CustomerWaybills = lazy(() => import('@features/customers/pages/CustomerWaybills'));
const BankAccountsList = lazy(() => import('@features/bank-accounts/pages/BankAccountsList'));
const TransceiversList = lazy(() => import('@features/transceivers/pages/TransceiversList'));
const CertificatesList = lazy(() => import('@features/certificates/pages/CertificatesList'));
const WithdrawRequestsList = lazy(() => import('@features/withdraw-requests/pages/WithdrawRequestsList'));
const TicketsList = lazy(() => import('@features/tickets/pages/TicketsList'));
const PaymentCallback = lazy(() => import('@features/payments/pages/PaymentCallback'));
const FinancialUnitGuide = lazy(() => import('@features/accounting/pages/FinancialUnitGuide'));
const PosDevicesPage = lazy(() => import('@features/pos-devices/pages/PosDevicesPage'));
const PosPaymentReportPage = lazy(() => import('@features/pos-devices/pages/PosPaymentReportPage'));
const SuspiciousWaybills = lazy(() => import('@features/waybills/pages/SuspiciousWaybills'));
const FareInquiry = lazy(() => import('@features/waybills/pages/FareInquiry'));
const RecoverRemittanceByIntroduction = lazy(() => import('@features/remittance/pages/RecoverRemittanceByIntroduction'));

// Placeholder components for routes that will be migrated
const Placeholder = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <h2 className="text-2xl font-bold mb-4">در حال توسعه...</h2>
      <p>این صفحه به زودی اضافه خواهد شد</p>
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Suspense fallback={<LoadingSpinner />}><HomeEntry /></Suspense>,
  },
  {
    path: '/welcome',
    element: <Suspense fallback={<LoadingSpinner />}><LandingPage /></Suspense>,
  },
  // Payment callback - redirect from payment gateway
  {
    path: '/payments',
    element: (
      <PublicLayout>
        <Suspense fallback={<LoadingSpinner />}>
          <PaymentCallback />
        </Suspense>
      </PublicLayout>
    ),
  },
  // Public Routes - API Documentation
  {
    path: '/endpoints',
    element: (
      <PublicLayout>
        <Suspense fallback={<LoadingSpinner />}>
          <Endpoints />
        </Suspense>
      </PublicLayout>
    ),
  },
  // SurMap Route - Raw OpenStreetMap test
  {
    path: '/surmap',
    element: (
      <PublicLayout>
        <Suspense fallback={<LoadingSpinner />}>
          <SurMap />
        </Suspense>
      </PublicLayout>
    ),
  },
  {
    path: '/login_password',
    element: (
      <AppLayoutAuth>
        <Suspense fallback={<LoadingSpinner />}>
          <Login />
        </Suspense>
      </AppLayoutAuth>
    ),
  },
  {
    path: '/Adminlogin_pass',
    element: <Navigate to="/login_password" replace />,
  },
  {
    path: '/login',
    element: <Navigate to="/login_password" replace />,
  },
  {
    path: '/auth/register',
    element: (
      <AppLayoutAuth>
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterUser />
        </Suspense>
      </AppLayoutAuth>
    ),
  },
  {
    path: '/auth/registerCompany',
    element: (
      <AppLayoutAuth>
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterCompany />
        </Suspense>
      </AppLayoutAuth>
    ),
  },
  {
    path: '/auth/registerFactory',
    element: (
      <AppLayoutAuth>
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterFactory />
        </Suspense>
      </AppLayoutAuth>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <DashboardWithTabs />
          </Suspense>
        ),
      },
      {
        path: 'user-activity',
        element: <Suspense fallback={<LoadingSpinner />}><UserActivity /></Suspense>,
      },
      {
        path: 'change-password',
        element: <Suspense fallback={<LoadingSpinner />}><ChangePassword /></Suspense>,
      },
      {
        path: 'wallet',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <WalletPage />
          </Suspense>
        ),
      },
      {
        path: 'financial-guide',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_ACCOUNTING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <FinancialUnitGuide />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Waybills routes
      {
        path: 'fare-inquiry',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_REMITTANCE, PERMISSIONS.LIST_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <FareInquiry />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybillsSearch',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_BILL_LOADING]}>
            <Placeholder />
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'trash',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <TrashPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybillsSearch',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/new',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/new/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybill-show/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ViewWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybillEdit/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybill/edit/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/edit/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateWaybill />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/history/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillHistory />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/payments/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillPayments />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'waybills/print/:id',
        element: (
          <PublicLayout>
            <Suspense fallback={<LoadingSpinner />}>
              <PrintWaybill />
            </Suspense>
          </PublicLayout>
        ),
      },
      // Remittance routes
      {
        path: 'remittance/recover-by-introduction',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.REMITTANCE_INTRODUCTION_LETTER, PERMISSIONS.CREATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <RecoverRemittanceByIntroduction />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remitanceSearch',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_REMITTANCE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_REMITTANCE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance-show/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <Remittance />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/edit/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_REMITTANCE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <Remittance />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/tracking/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceTracking />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/history/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceHistory />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/payments/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittancePayments />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/discharge/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceDischarge />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/print/:id',
        element: (
          <PublicLayout>
            <Suspense fallback={<LoadingSpinner />}>
              <PrintRemittance />
            </Suspense>
          </PublicLayout>
        ),
      },
      // Remittance routes
      {
        path: 'remittance/new',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_REMITTANCE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <Remittance />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Drivers routes
      {
        path: 'drivers',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_DRIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <DriversList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'drivers/detail/:driverId',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_DRIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <DriverDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'drivers/turns/:driverId',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_DRIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <DriverTurns />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'drivers/cars/:driverId',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_DRIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <DriverCars />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'drivers/offline',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_DRIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <DriverOffline />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Companies routes
      {
        path: 'companies',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'companies/new',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_COMPANY]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateCompany />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'companies/detail/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_COMPANY]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CompanyDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'company/users',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_COMPANY_USER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CompanyUsersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'companies/users/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_COMPANY_USER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CompanyUsers />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'companies/profile/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_COMPANY]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CompanyProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'companies/admins/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_COMPANY_USER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CompanyAdmins />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Base Information routes
      {
        path: 'base-information/cities',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SHARED_CITY_CATALOG]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CitiesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/cars',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_CAR]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CarsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'branches',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_BRANCH]}>
            <Suspense fallback={<LoadingSpinner />}>
              <BranchesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/offices',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <OfficesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/goods',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SHARED_PRODUCT_CATALOG]}>
            <Suspense fallback={<LoadingSpinner />}>
              <GoodsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/product-owners',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ProductOwnersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/sender-receiver-search',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SenderReceiverSearch />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'transceivers',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_TRANSCEIVER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <TransceiversList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'financial-coefficients',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_FINANCIAL_COEFFICIENT, PERMISSIONS.CREATE_BILL_LOADING]}>
            <Suspense fallback={<LoadingSpinner />}>
              <FinancialCoefficients />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'financial-coefficients/create',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_FINANCIAL_COEFFICIENT, PERMISSIONS.CREATE_FINANCIAL_COEFFICIENT]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CreateFinancialCoefficient />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'financial-coefficients/edit/:id',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_FINANCIAL_COEFFICIENT, PERMISSIONS.UPDATE_FINANCIAL_COEFFICIENT]}>
            <Suspense fallback={<LoadingSpinner />}>
              <EditFinancialCoefficient />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'insurance-contracts',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <InsuranceContracts />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'insurance-contracts/new',
        element: <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_INSURANCE_CONTRACT]}><Suspense fallback={<LoadingSpinner />}><InsuranceContractEditor /></Suspense></ProtectedRoute>,
      },
      {
        path: 'insurance-contracts/:id/edit',
        element: <ProtectedRoute requiredPermissions={[PERMISSIONS.UPDATE_INSURANCE_CONTRACT]}><Suspense fallback={<LoadingSpinner />}><InsuranceContractEditor /></Suspense></ProtectedRoute>,
      },
      {
        path: 'waybill-numbers',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillNumbers />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'base-information/drivers-search',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <DriversSearch />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Settings routes
      {
        path: 'settings/user-print-stamps',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_USER_PRINT_STAMPS]}>
            <Suspense fallback={<LoadingSpinner />}><PrintStampAudit /></Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/print-stamps',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <PrintStamps />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <Settings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'pos-devices/reports',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <PosPaymentReportPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'remittance/drafts',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_REMITTANCE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <RemittanceDrafts />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'suspicious-waybills',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SuspiciousWaybills />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'pos-devices',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_POS_DEVICE, PERMISSIONS.ASSIGN_POS_DEVICE_USERS]}>
            <Suspense fallback={<LoadingSpinner />}>
              <PosDevicesPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Role Management routes
      {
        path: 'roles',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <RolesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Customer Management routes (create/edit are modals on the list page)
      {
        path: 'customers/secondary-debt-settings',
        element: <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_SECONDARY_CUSTOMER_DEBT]}><Suspense fallback={<LoadingSpinner />}><SecondaryDebtSettings /></Suspense></ProtectedRoute>,
      },
      {
        path: 'customers/:id/waybills',
        element: <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_CUSTOMER, PERMISSIONS.MANAGE_ISSUER_FINANCIAL_STATUS, PERMISSIONS.VIEW_ISSUER_DEBT_ALLOCATIONS]}><Suspense fallback={<LoadingSpinner />}><CustomerWaybills /></Suspense></ProtectedRoute>,
      },
      {
        path: 'waybills/:id/choose-debtor',
        element: <ProtectedRoute><Suspense fallback={<LoadingSpinner />}><ChooseWaybillDebtor /></Suspense></ProtectedRoute>,
      },
      {
        path: 'customers',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_CUSTOMER]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CustomersList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'customers/new',
        element: <Navigate to="/customers" replace />,
      },
      {
        path: 'customers/edit/:id',
        element: <Navigate to="/customers" replace />,
      },
      // Bank Accounts routes
      {
        path: 'bank-accounts',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_BANK_ACCOUNT]}>
            <Suspense fallback={<LoadingSpinner />}>
              <BankAccountsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Certificates routes
      {
        path: 'certificates',
        element: (
          <ProtectedRoute requiredPermissions={[PERMISSIONS.LIST_CERTIFICATE]}>
            <Suspense fallback={<LoadingSpinner />}>
              <CertificatesList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Withdraw Requests routes
      {
        path: 'withdraw-requests',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <WithdrawRequestsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Tickets/Support routes
      {
        path: 'tickets',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <TicketsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // Reports routes
      {
        path: 'reports/financial',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <WaybillFinancialReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/tonnage',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <TonnageReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <ReportsList />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/summary',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SummaryReports />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/pay-the-fare',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <PayTheFareReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/expense',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <OperationalWaybillReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/summary-of-coefficients',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/cargo-summary',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/sender-summary',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/receiver-summary',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/loader-type-summary',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <SpecialSummaryReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/insurance',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <OperationalWaybillReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports/tax-duties',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<LoadingSpinner />}>
              <OperationalWaybillReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Admin Panel routes — فقط ادمین
  {
    path: '/admin',
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      {
        path: 'cities',
        element: <Suspense fallback={<LoadingSpinner />}><CitiesList /></Suspense>,
      },
      {
        path: 'goods',
        element: <Suspense fallback={<LoadingSpinner />}><GoodsList /></Suspense>,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminDashboard />
          </Suspense>
        ),
      },
      {
        path: 'companies',
        element: <Navigate to="/admin/dashboard" replace />,
      },
      {
        path: 'pos-devices',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PosDevicesPage adminMode />
          </Suspense>
        ),
      },
      {
        path: 'companies/new',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CreateCompany />
          </Suspense>
        ),
      },
      {
        path: 'companies/detail/:id',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CompanyDetail />
          </Suspense>
        ),
      },
      {
        path: 'users',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminUsers />
          </Suspense>
        ),
      },
      {
        path: 'admins',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminAdmins />
          </Suspense>
        ),
      },
      {
        path: 'roles',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminRoles />
          </Suspense>
        ),
      },
      {
        path: 'permissions',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminPermissions />
          </Suspense>
        ),
      },
      {
        path: 'settings',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminSettings />
          </Suspense>
        ),
      },
      {
        path: 'logs',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminLogs />
          </Suspense>
        ),
      },
      {
        path: 'tickets',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AdminTickets />
          </Suspense>
        ),
      },
    ],
  },
  // Catch all - redirect to home
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
  },
});

const AppRouter: React.FC = () => {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};

export default AppRouter;
